import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2 } from "lucide-react";

interface PollOption {
  id: string;
  option_text: string;
  votes_count: number;
}

interface PostPollProps {
  postId: string;
}

export function PostPoll({ postId }: PostPollProps) {
  const [pollId, setPollId] = useState<string | null>(null);
  const [pollQuestion, setPollQuestion] = useState<string | null>(null);
  const [options, setOptions] = useState<PollOption[]>([]);
  const [userVoteOptionId, setUserVoteOptionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);

  useEffect(() => {
    loadPoll();
  }, [postId]);

  const loadPoll = async () => {
    try {
      // Get poll for this post
      const { data: poll } = await supabase
        .from('community_polls')
        .select('id, question')
        .eq('post_id', postId)
        .maybeSingle() as any;

      if (!poll) {
        setLoading(false);
        return;
      }

      setPollId(poll.id);
      setPollQuestion(poll.question || null);

      // Get options
      const { data: opts } = await supabase
        .from('community_poll_options')
        .select('id, option_text, votes_count')
        .eq('poll_id', poll.id)
        .order('created_at');

      setOptions(opts || []);

      // Check user vote
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: vote } = await supabase
          .from('community_poll_votes')
          .select('option_id')
          .eq('poll_id', poll.id)
          .eq('user_id', user.id)
          .maybeSingle();

        setUserVoteOptionId(vote?.option_id || null);
      }
    } catch (err) {
      console.error('Error loading poll:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (optionId: string) => {
    if (voting) return;
    setVoting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !pollId) return;

      if (userVoteOptionId) {
        // Remove existing vote
        await supabase
          .from('community_poll_votes')
          .delete()
          .eq('poll_id', pollId)
          .eq('user_id', user.id);

        if (userVoteOptionId === optionId) {
          setUserVoteOptionId(null);
          await loadPoll();
          return;
        }
      }

      // Cast new vote
      await supabase
        .from('community_poll_votes')
        .insert({ poll_id: pollId, option_id: optionId, user_id: user.id });

      setUserVoteOptionId(optionId);
      await loadPoll();
    } catch (err) {
      console.error('Error voting:', err);
    } finally {
      setVoting(false);
    }
  };

  if (loading || !pollId) return null;

  const totalVotes = options.reduce((sum, o) => sum + (o.votes_count || 0), 0);
  const hasVoted = !!userVoteOptionId;

  return (
    <div className="space-y-2 p-3 border border-border rounded-lg bg-muted/20">
      {pollQuestion && (
        <p className="text-sm font-semibold text-foreground mb-1">{pollQuestion}</p>
      )}
      {options.map((opt) => {
        const pct = totalVotes > 0 ? Math.round((opt.votes_count / totalVotes) * 100) : 0;
        const isSelected = opt.id === userVoteOptionId;

        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => handleVote(opt.id)}
            disabled={voting}
            className="w-full text-left"
          >
            <div className={`relative rounded-lg border p-3 transition-colors ${
              isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
            }`}>
              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center gap-2">
                  {isSelected && <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />}
                  <span className="text-sm font-medium text-foreground">{opt.option_text}</span>
                </div>
                {hasVoted && (
                  <span className="text-xs text-muted-foreground font-medium">{pct}%</span>
                )}
              </div>
              {hasVoted && (
                <div className="absolute inset-0 rounded-lg overflow-hidden">
                  <div
                    className="h-full bg-primary/10 transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              )}
            </div>
          </button>
        );
      })}
      <p className="text-xs text-muted-foreground text-center">
        {totalVotes} {totalVotes === 1 ? 'voto' : 'votos'}
      </p>
    </div>
  );
}
